import { useEffect, useRef, useState } from 'react';
import data from '@emoji-mart/data';
import Picker from '@emoji-mart/react';
import PropTypes from 'prop-types';
import './emoji-editor.scss';
import { clamp, useImageDisplayArea, useTextDrag } from './utils';

const EMOJI_SIZE = { MIN: 32, MAX: 144, STEP: 8, DEFAULT: 72 };

let _idCounter = 1;
const generateId = () => _idCounter++;

const EmojiEditor = ({ file, imgSrc, onEditComplete, onRegisterApply, initialEmojiItems, onEmojiItemsChange }) => {
    const containerRef = useRef(null);
    const mediaElementRef = useRef(null);
    const applyFnRef = useRef(null);

    const { current: seedItems } = useRef((initialEmojiItems ?? []).map(item => ({ ...item, id: generateId() })));

    const [emojiItems, setEmojiItems] = useState(seedItems);
    const [activeEmojiId, setActiveEmojiId] = useState(seedItems[seedItems.length - 1]?.id ?? null);
    const [showPicker, setShowPicker] = useState(seedItems.length === 0);
    const [pickerTarget, setPickerTarget] = useState('new');

    const { displayArea, onMediaLoad } = useImageDisplayArea(containerRef, mediaElementRef);

    const handlePositionChange = (itemId, newPosition) => {
        setEmojiItems(prev => prev.map(item => (item.id === itemId ? { ...item, position: newPosition } : item)));
    };

    const { onDragStart, onDragMove, onDragEnd } = useTextDrag(
        containerRef,
        displayArea,
        handlePositionChange,
        setActiveEmojiId,
    );

    const resizeState = useRef(null);

    const onResizeStart = (e, itemId, startSize, position) => {
        e.stopPropagation();
        if (!displayArea) return;
        const containerRect = containerRef.current.getBoundingClientRect();
        const source = e.touches ? e.touches[0] : e;
        const pointerX = source.clientX - containerRect.left;
        const pointerY = source.clientY - containerRect.top;
        const centerX = displayArea.left + (position.x / 100) * displayArea.width;
        const centerY = displayArea.top + (position.y / 100) * displayArea.height;
        const startDist = Math.hypot(pointerX - centerX, pointerY - centerY);
        resizeState.current = { itemId, startSize, startDist, centerX, centerY };
    };

    const onResizeMove = e => {
        if (!resizeState.current) return;
        const { itemId, startSize, startDist, centerX, centerY } = resizeState.current;
        if (startDist === 0) return;
        const containerRect = containerRef.current.getBoundingClientRect();
        const source = e.touches ? e.touches[0] : e;
        const pointerX = source.clientX - containerRect.left;
        const pointerY = source.clientY - containerRect.top;
        const currentDist = Math.hypot(pointerX - centerX, pointerY - centerY);
        const newSize = clamp(Math.round(startSize * (currentDist / startDist)), EMOJI_SIZE.MIN, EMOJI_SIZE.MAX);
        setEmojiItems(prev => prev.map(item => (item.id === itemId ? { ...item, size: newSize } : item)));
    };

    const onResizeEnd = () => {
        resizeState.current = null;
    };

    applyFnRef.current = () =>
        new Promise(resolve => {
            if (!imgSrc) {
                resolve(null);
                return;
            }

            const img = new window.Image();
            img.src = imgSrc;
            img.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width = img.naturalWidth;
                canvas.height = img.naturalHeight;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0);

                emojiItems.forEach(item => {
                    const x = (item.position.x / 100) * canvas.width;
                    const y = (item.position.y / 100) * canvas.height;
                    const scaledSize = item.size * (canvas.width / (displayArea?.width ?? 500));
                    ctx.font = `${Math.max(scaledSize, 32)}px serif`;
                    ctx.textAlign = 'center';
                    ctx.textBaseline = 'middle';
                    ctx.fillText(item.emoji, x, y);
                });

                canvas.toBlob(
                    blob => {
                        if (!blob) {
                            resolve(null);
                            return;
                        }
                        const editedFile = new File([blob], file.name, {
                            type: file.type,
                            lastModified: Date.now(),
                        });
                        onEmojiItemsChange?.(emojiItems);
                        onEditComplete?.(editedFile);
                        resolve(editedFile);
                    },
                    file.type || 'image/jpeg',
                    0.92,
                );
            };
        });

    useEffect(() => {
        onRegisterApply?.(async () => await applyFnRef.current?.());
        return () => onRegisterApply?.(null);
    }, [onRegisterApply]);

    const handleEmojiSelect = selected => {
        if (pickerTarget === 'new') {
            const newItem = {
                id: generateId(),
                emoji: selected.native,
                size: EMOJI_SIZE.DEFAULT,
                position: { x: 50, y: 45 },
            };
            setEmojiItems(prev => [...prev, newItem]);
            setActiveEmojiId(newItem.id);
        } else {
            setEmojiItems(prev =>
                prev.map(item => (item.id === pickerTarget ? { ...item, emoji: selected.native } : item)),
            );
            setActiveEmojiId(pickerTarget);
        }
        setShowPicker(false);
    };

    const handleAddEmoji = () => {
        setPickerTarget('new');
        setShowPicker(true);
    };

    const handleChangeEmoji = () => {
        if (!activeEmojiId) return;
        setPickerTarget(activeEmojiId);
        setShowPicker(true);
    };

    const handleDeleteEmoji = () => {
        if (!activeEmojiId) return;
        const remaining = emojiItems.filter(item => item.id !== activeEmojiId);
        setEmojiItems(remaining);
        if (remaining.length === 0) {
            setActiveEmojiId(null);
            setPickerTarget('new');
            setShowPicker(true);
        } else {
            setActiveEmojiId(remaining[remaining.length - 1].id);
        }
    };

    return (
        <div className="se-emoji-editor">
            <div
                className="se-emoji-wrapper"
                ref={containerRef}
                onMouseMove={e => {
                    onDragMove(e);
                    onResizeMove(e);
                }}
                onMouseUp={() => {
                    onDragEnd();
                    onResizeEnd();
                }}
                onMouseLeave={() => {
                    onDragEnd();
                    onResizeEnd();
                }}
                onTouchMove={e => {
                    onDragMove(e);
                    onResizeMove(e);
                }}
                onTouchEnd={() => {
                    onDragEnd();
                    onResizeEnd();
                }}
            >
                <img
                    ref={mediaElementRef}
                    src={imgSrc}
                    alt=""
                    className="se-ee-bg"
                    draggable={false}
                    onLoad={onMediaLoad}
                />

                {displayArea &&
                    emojiItems.map(item => (
                        <div
                            key={item.id}
                            className={`se-ee-overlay${item.id === activeEmojiId ? ' active' : ''}`}
                            style={{
                                left: displayArea.left + (item.position.x / 100) * displayArea.width,
                                top: displayArea.top + (item.position.y / 100) * displayArea.height,
                                fontSize: item.size,
                            }}
                            onMouseDown={e => onDragStart(e, item.id, item.position)}
                            onTouchStart={e => onDragStart(e, item.id, item.position)}
                        >
                            {item.emoji}
                            {item.id === activeEmojiId && (
                                <span
                                    className="se-ee-resize-handle"
                                    onMouseDown={e => onResizeStart(e, item.id, item.size, item.position)}
                                    onTouchStart={e => onResizeStart(e, item.id, item.size, item.position)}
                                />
                            )}
                        </div>
                    ))}
                {showPicker && (
                    <div className="se-ee-picker-sheet">
                        <Picker
                            data={data}
                            onEmojiSelect={handleEmojiSelect}
                            theme="dark"
                            perLine={8}
                            emojiSize={24}
                            searchPosition="none"
                            previewPosition="none"
                            skinTonePosition="none"
                        />
                    </div>
                )}
            </div>
            {!showPicker && (
                <div className="se-ee-bottom">
                    <button
                        type="button"
                        className={`se-ee-tool-btn${!activeEmojiId ? ' se-ee-hidden' : ''}`}
                        onClick={handleDeleteEmoji}
                        aria-label="Delete emoji"
                    >
                        <svg viewBox="0 0 24 24" width="17" height="17" fill="currentColor">
                            <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
                        </svg>
                    </button>

                    <button
                        type="button"
                        className={`wa-ee-change-btn${!activeEmojiId ? ' wa-ee-hidden' : ''}`}
                        onClick={handleChangeEmoji}
                    >
                        Change
                    </button>

                    <button type="button" className="se-ee-add-btn" onClick={handleAddEmoji} title="Add another emoji">
                        + Add
                    </button>
                </div>
            )}
        </div>
    );
};

EmojiEditor.propTypes = {
    file: PropTypes.object.isRequired,
    imgSrc: PropTypes.string.isRequired,
    onEditComplete: PropTypes.func,
    onRegisterApply: PropTypes.func,
    initialEmojiItems: PropTypes.array,
    onEmojiItemsChange: PropTypes.func,
};

export default EmojiEditor;
